import { decode } from 'html-entities';
import Link from 'next/link';
import { GetStaticPropsContext } from 'next';
import React from 'react';

import Line from 'components/Line';
import { Sitemap, SitemapPath, getSitemap } from '../server/services/Sitemap';
import { getUrlAsTitle } from 'utils/utils';
import { withPageStaticProps } from 'utils/header';

interface Url extends SitemapPath {
  url: string;
}

interface GroupedLinks {
  [key: string]: LinkGroup;
}

interface LinkGroup {
  links: Url[];
  header: string;
}

const getGroupHeader = (group: string, links: Url[]) => {
  const matchingLink = links.find(({ path }) => path === `/${group}`);

  return matchingLink?.title ?? getUrlAsTitle(group);
};

const getTopLevelOfPath = (path: string) => {
  const parts = path.split('/').filter((part) => part.trim() !== '');

  return parts[0] ?? '/';
};

const getGroupedLinks = (links: Url[]) =>
  links.reduce((cache: GroupedLinks, link) => {
    const group = getTopLevelOfPath(link.path);

    if (!cache.hasOwnProperty(group)) {
      cache[group] = {
        links: [],
        header: getGroupHeader(group, links),
      };
    }

    cache[group]['links'].push(link);

    return cache;
  }, {});

const getPathsWithUrl = (pages: Sitemap) => {
  return pages.map(
    (entry): Url => ({
      ...entry,
      url: `${process.env.NEXT_PUBLIC_LOCAL_URL}${entry.path}`,
    }),
  );
};

const withSinglePagesRegrouped = (groupedLinks: GroupedLinks) => {
  const entries = Object.entries(groupedLinks);

  const regroupedLinks = entries.reduce(
    (acc, [directory, group]) => {
      if (group.links.length === 1) {
        acc.topLevelPages.links.push(group.links[0]);
      } else {
        acc[directory] = group;
      }

      return acc;
    },
    {
      topLevelPages: {
        header: 'Pages',
        links: [],
      },
    } as GroupedLinks,
  );

  return regroupedLinks;
};

interface Props {
  groupedLinks: GroupedLinks;
}

export default class SiteMap extends React.Component<Props> {
  render() {
    const { groupedLinks } = this.props;

    return (
      <div className="m-section-hero container">
        <h1 className="text-h1-display">Sitemap</h1>
        <Line />
        <div className="grid grid-cols-12 sm:gap-32">
          {Object.entries(groupedLinks).map(([directory, group], index) => (
            <div key={index} className="col-span-12 space-y-16 sm:col-span-6 md:col-span-4 lg:col-span-3">
              <h2 key={directory} className="text-h4-display">
                {group.header}
              </h2>

              <ul>
                {group.links.map(({ url, title }) => (
                  <li key={url} className="text-sm">
                    <Link href={url} prefetch={false} className={'tracker-link-sitemap-link-20210510-143621'}>
                      {decode(title)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export const getStaticProps = withPageStaticProps(async (context: GetStaticPropsContext) => {
  const sitemap = await getSitemap();

  const groupedLinks = getGroupedLinks(getPathsWithUrl(sitemap));

  return {
    props: {
      groupedLinks: withSinglePagesRegrouped(groupedLinks),
    },
  };
});
